/**
 * 2차 AI 입시분석 워크스페이스 GNB·랜딩 축 선언 배열 SSoT.
 *
 * WorkspaceShell(헤더 진행 레일 — 순번·재료 매핑은 workspaceRail.ts 파생)과 랜딩(`landingAxes.ts` 가 이 배열을 파생해 축 카드로 그림)이 이 단일
 * 배열을 공유한다(항목 추가 = 배열 1행, 하드코딩 금지). 034 가 "2차 오픈 시 후속"으로 미뤘던 landingAxes↔workspaceNav 이중 선언은 075 에서 여기로
 * 통합됐다 — 축명·설명·아이콘·대표 route 를 다른 파일에 다시 쓰지 말 것.
 *
 * ⚠️ 도메인 데이터라 config/(도메인 무지 인프라)가 아닌 data/analysis/ 에 둔다
 *    (docs/frontend-domain-structure-proposal.md §71 — components/layout·ui·lib·config 성격은 도메인 무지).
 *
 * 대분류 4축: 내 입시카드 / 성적 분석 / 진로심리검사(노출 보류 — visibility.ts) / 리포트 보관함.
 * 외부 LLM 기반 "AI 종합분석" 축은 제거됐다 — 분석은 전부 서버 규칙 기반이다.
 *
 * 활성(status:'active')은 내 입시카드 3항목·정시/수시 분석·진로심리검사 4종·리포트 보관함.
 * 나머지는 status:'soon'(준비중, 클릭 비활성)으로 앞으로 붙어 나갈 로드맵을 드러낸다.
 */

import {
  ARCHIVE_KINDS,
  REPORT_REGISTRY,
  isArchiveKind,
  reportArchiveRoute,
} from './reportRegistry';
import { isNavVisible } from './visibility';

/** 하위 기능 항목. active 만 route 를 가지며, soon 은 "준비중" 비활성 표시. */
export interface WorkspaceSubItem {
  /** 하위 기능명(서술형). */
  label: string;
  /** 활성 항목의 라우트 경로. soon 항목은 미보유(클릭 비활성). */
  route?: string;
  /** active=이동 가능 / soon=준비중(비활성). */
  status: 'active' | 'soon';
  /** 드롭다운·카드에 노출할 보조 설명(선택). */
  desc?: string;
}

/** 대분류 식별자 — 라벨은 바뀔 수 있어 코드 참조는 key 로 한다(랜딩 축 key 와 동일). */
export type WorkspaceNavKey = 'card' | 'score' | 'test' | 'reports';

/** 랜딩 축 카드 전용 표시값 — GNB 는 읽지 않는다. */
export interface WorkspaceLandingCard {
  /** 카드 우하단 태그. */
  tag: string;
  /** 하위 기능 칩(체크 아이콘) — 축 안에 무엇이 있는지 드러낸다. */
  sub?: string[];
}

/** GNB 대분류(컨설팅 목적 축). */
export interface WorkspaceNavGroup {
  key: WorkspaceNavKey;
  /** 대분류명(서술형). */
  label: string;
  /** 진입 홈 카드 헤더 아이콘(lucide-vue-next 아이콘 컴포넌트명 kebab → 매핑은 페이지에서). */
  icon: WorkspaceNavIcon;
  /** 이 대분류를 대표하는 한 줄 설명(진입 홈 카드용). */
  summary: string;
  /** 대표 진입 route — 랜딩 축 카드 클릭 목적지(하위 항목 route 와 별개). */
  route: string;
  /** 랜딩 축 카드 표시값. */
  landing: WorkspaceLandingCard;
  /** 하위 기능 목록. */
  items: WorkspaceSubItem[];
}

/** 대분류 아이콘 키(홈 카드에서 lucide 컴포넌트로 매핑). */
export type WorkspaceNavIcon = 'clipboard' | 'target' | 'list-checks' | 'archive';

/** 내 입시카드(WS-01) — 성적 입력 허브. 분석 진입 전 성적이 없으면 여기로 보낸다. */
export const IPSI_CARD_ROUTE = '/card';
/** 성적 분석(정시) — 저장된 수능 성적을 소비하는 분석 화면. */
export const QUANT_JEONGSI_ROUTE = '/jeongsi';
/** 수시 6장 전략 생성 화면(060). */
export const SUSI_CARDS_ROUTE = '/susi/cards';

export const WORKSPACE_NAV: WorkspaceNavGroup[] = [
  {
    // 모든 분석이 공유하는 내 성적·희망 정보. 한 번 저장하면 재입력 불필요.
    key: 'card',
    label: '내 입시카드',
    icon: 'clipboard',
    summary: '성적과 학교 정보를 한 번만 저장하면, 다음부터는 다시 안 물어봐요.',
    route: IPSI_CARD_ROUTE,
    landing: {
      tag: '여기서 시작',
      sub: ['수능 백분위·내신 등급', '학년·가고 싶은 계열', '고치면 전부 같이 바뀜'],
    },
    // WS-01(035) 오픈 — 3섹션이 `/card` **단일 화면**이라 하위 항목은 같은 화면의
    // 앵커로 연결한다(IA D-3 확정). 앵커 id 는 CardSection 의 `anchor` prop 이 소유.
    items: [
      {
        label: '기본 정보',
        route: `${IPSI_CARD_ROUTE}#basic`,
        status: 'active',
        desc: '학년·희망 계열·고교 정보.',
      },
      {
        label: '수능 성적 입력',
        route: `${IPSI_CARD_ROUTE}#suneung`,
        status: 'active',
        desc: '국어·수학·탐구 백분위를 넣으면 정시 분석에 쓰입니다.',
      },
      // ⚖️ 내신은 학생 수동 입력만 가능 — 학생부 HTML·PDF 업로드/OCR 자동입력은 법령상 불가.
      {
        label: '내신 성적 입력',
        route: `${IPSI_CARD_ROUTE}#naesin`,
        status: 'active',
        desc: '교과 등급을 넣으면 수시 분석에 쓰입니다.',
      },
      // ⚖️ 폐기 — 비교과(서술형) 분석·평가는 「초·중등교육법」 제25조의2 위반.
      //    근거: claudedocs/legal/02-학생부-비교과-이용제한.md (오픈 검토 전 반드시 읽을 것).
    ],
  },
  {
    key: 'score',
    label: '성적 분석',
    icon: 'target',
    summary: '지금 성적으로 어느 대학·학과까지 노려볼 수 있는지 정시·수시로 나눠서 봅니다.',
    route: QUANT_JEONGSI_ROUTE,
    // 학과별 규칙 해석·수시 6장 전략(060)은 이 축 안에 있다 — 랜딩 칩으로 드러낸다(075).
    landing: {
      tag: '어디까지 갈 수 있나',
      sub: ['합격선까지 얼마나 남았나', '학과별 점수 계산법 풀이', '수시 6장 어떻게 채울까'],
    },
    items: [
      {
        label: '정시 분석',
        route: QUANT_JEONGSI_ROUTE,
        status: 'active',
        desc: '수능 성적과 예상 합격선을 비교합니다.',
      },
      {
        label: '수시 분석',
        route: '/susi',
        status: 'active',
        desc: '전형별로 어떤 등급이 붙었는지, 뭘 준비해야 하는지.',
      },
      // "진단 결과" 항목 제거(IA D-5) — 진단은 분석 화면에서 생성→상세 이동, 이력은 내 리포트 귀속.
    ],
  },
  {
    key: 'test',
    label: '진로심리검사',
    icon: 'list-checks',
    summary: '성적표에는 안 나오는 내 관심사와 공부 습관을 검사로 알아봅니다.',
    route: '/tests',
    landing: {
      tag: '성적 말고 나',
      sub: ['관심사로 찾는 학과', '내 공부 습관 알기', '지금 준비는 잘 되고 있나'],
    },
    // 4종 검사가 `/tests` 한 화면에 모여 있어 메뉴도 한 항목으로 합친다 — 같은 곳으로 가는
    // 항목을 종류 수만큼 늘어놓으면 별도 화면처럼 오인된다(검사 목록은 화면 안에서 고른다).
    items: [
      {
        label: '검사 받기',
        route: '/tests',
        status: 'active',
        desc: '진로·학습 전략·심리·진학 준비도 4종.',
      },
    ],
  },
  {
    // 리포트 보관함(065 WS-13) — 불변 리포트의 유일한 이력 허브. 하위 항목은 레지스트리 순 노출 종류
    // 필터 딥링크(`?kind=`)이며 라벨·설명도 레지스트리 SSoT 에서 읽는다(재기입 금지).
    key: 'reports',
    label: '리포트 보관함',
    icon: 'archive',
    summary: '지금까지 만든 결과는 전부 남아 있어요. 언제든 다시 열어볼 수 있습니다.',
    route: reportArchiveRoute(),
    landing: { tag: '지난 결과 보기' },
    items: ARCHIVE_KINDS.map((kind) => ({
      label: REPORT_REGISTRY[kind].label,
      route: reportArchiveRoute(kind),
      status: 'active' as const,
      desc: REPORT_REGISTRY[kind].desc,
    })),
  },
];

/**
 * 화면에 실제로 노출하는 축 — `visibility.ts` 의 숨김 플래그를 적용한 파생. GNB 레일·랜딩 축 카드는 이 배열을 읽는다.
 * `WORKSPACE_NAV` 는 선언 전체(breadcrumb 라벨·숨긴 축의 직접 진입 판정용)라 그대로 둔다.
 */
export const VISIBLE_WORKSPACE_NAV: WorkspaceNavGroup[] = WORKSPACE_NAV.filter((group) =>
  isNavVisible(group.key),
);

/** breadcrumb 한 마디(대분류/현재 화면). route 있으면 링크, 없으면 텍스트. */
export interface WorkspaceCrumb {
  label: string;
  route?: string;
}

interface WorkspacePageLocation {
  groupLabel: string;
  /** 대분류 crumb 의 복귀 링크(선택) — 보관함 귀속 상세는 해당 종류 필터로 돌아간다(065). */
  groupRoute?: string;
  pageLabel: string;
  /** 대분류와 현재 화면 사이의 중간 조각(062 US3) — route 를 채우면 링크로 렌더된다. */
  middle?: WorkspaceCrumb;
  /** 경로·해시·쿼리로 현재 화면을 판정한다 — `?kind=` 필터 화면은 query 로 라벨을 가른다(065). */
  matches: (path: string, hash: string, query: URLSearchParams) => boolean;
}

/** 보관함 그룹 라벨 — 리포트 상세·종류 필터 화면이 전부 이 그룹에 귀속된다(065 FR-020). */
const ARCHIVE_GROUP = '리포트 보관함';

/** 보관함 필터 화면의 pageLabel — `?kind=` 가 노출 종류면 그 라벨, 아니면 그룹 라벨(전체). */
function archivePageLabel(query: URLSearchParams): string {
  const kind = query.get('kind');
  return isArchiveKind(kind) ? REPORT_REGISTRY[kind].label : ARCHIVE_GROUP;
}

/** 메뉴 항목과 독립적인 실제 페이지·탭 위치 라벨 SSoT. 구체 경로를 접두 경로보다 먼저 둔다. */
const WORKSPACE_PAGE_LOCATIONS: WorkspacePageLocation[] = [
  // 수시 6장 리포트 재열람(060·065) — 불변 리포트 상세라 보관함 귀속. 대분류 crumb 이 필터 복귀 링크.
  {
    groupLabel: ARCHIVE_GROUP,
    groupRoute: reportArchiveRoute('SUSI_CARDS'),
    pageLabel: REPORT_REGISTRY.SUSI_CARDS.label,
    matches: (path) => /^\/\/susi\/cards\/\d+$/.test(path),
  },
  // 수시 6장 생성 화면 — 정확 일치만(재열람은 위 매처가 먼저 잡는다).
  {
    groupLabel: '성적 분석',
    pageLabel: '수시 6장 전략',
    middle: { label: '수시 분석', route: '/susi' },
    matches: (path) => path === SUSI_CARDS_ROUTE,
  },
  {
    groupLabel: ARCHIVE_GROUP,
    groupRoute: reportArchiveRoute('DIAGNOSIS'),
    pageLabel: REPORT_REGISTRY.DIAGNOSIS.label,
    matches: (path) => path.startsWith('/diagnosis/'),
  },
  {
    groupLabel: ARCHIVE_GROUP,
    groupRoute: reportArchiveRoute('ASSESSMENT'),
    pageLabel: REPORT_REGISTRY.ASSESSMENT.label,
    matches: (path) => path.startsWith('/results/'),
  },
  // 보관함 필터 화면 — pageLabel 은 `?kind=` 로 결정(전체면 그룹 라벨 = 한 마디만).
  {
    groupLabel: ARCHIVE_GROUP,
    groupRoute: '/reports',
    pageLabel: ARCHIVE_GROUP,
    matches: (path) => path === '/reports',
  },
  {
    groupLabel: '내 입시카드',
    pageLabel: '내 입시카드',
    matches: (path) => path === IPSI_CARD_ROUTE,
  },
  {
    groupLabel: '성적 분석',
    pageLabel: '정시 분석',
    matches: (path) => path === QUANT_JEONGSI_ROUTE,
  },
  {
    groupLabel: '성적 분석',
    pageLabel: '수시 분석',
    matches: (path) => path === '/susi',
  },
  {
    groupLabel: '진로심리검사',
    pageLabel: '진로심리검사',
    matches: (path) => path === '/tests' || path.startsWith('/tests/'),
  },
];

/**
 * 하위 페이지 route 로 소속 대분류·현재 화면을 역추적해 breadcrumb 조각을 만든다(SSoT 재사용).
 * WorkspaceBreadcrumb 이 '홈'을 앞에 붙이므로 여기선 [대분류, (중간), 현재화면]만 반환.
 *
 * 1차: 정확 일치(기존 규칙, GNB 등록 route 그대로 진입한 화면).
 * 동적 상세·hash 탭을 구체적인 순서로 판정하며, 매칭 실패 시 빈 배열을 반환한다.
 * 중간 조각(middle)에 route 가 있으면 WorkspaceBreadcrumb 이 RouterLink 로 렌더한다(062 US3).
 */
export function crumbsForRoute(fullPath: string): WorkspaceCrumb[] {
  const url = new URL(fullPath, 'https://workspace.local');
  const location = WORKSPACE_PAGE_LOCATIONS.find((candidate) =>
    candidate.matches(url.pathname, url.hash, url.searchParams),
  );
  if (!location) return [];
  // 보관함 필터 화면은 kind 쿼리가 페이지 라벨을 가른다(065) — 전체면 그룹 한 마디만.
  const pageLabel =
    url.pathname === '/reports' ? archivePageLabel(url.searchParams) : location.pageLabel;
  if (location.groupLabel === pageLabel) return [{ label: pageLabel }];
  const crumbs: WorkspaceCrumb[] = [{ label: location.groupLabel, route: location.groupRoute }];
  if (location.middle) crumbs.push(location.middle);
  crumbs.push({ label: pageLabel });
  return crumbs;
}
